export const siteCountries = [
  { id: "au", name: "Australia", languageTag: "AU" },
  { id: "nz", name: "New Zealand", languageTag: "NZ" },
  { id: "uk", name: "United Kingdom", languageTag: "GB" },
  { id: "fr", name: "France", languageTag: "FR" },
  { id: "ca", name: "Canada", languageTag: "CA" },
  { id: "us", name: "United States", languageTag: "US" },
] as const;
export const siteLanguages = [
  { id: "en", name: "English", preview: false },
  { id: "fr", name: "Français", preview: false },
  { id: "it", name: "Italiano", preview: false },
  { id: "es", name: "Español", preview: false },
  { id: "de", name: "Deutsch", preview: false },
  { id: "pt", name: "Português", preview: false },
] as const;
export type SiteCountry = (typeof siteCountries)[number]["id"];
export type SiteLanguage = (typeof siteLanguages)[number]["id"];
export interface SiteLocale {
  country: SiteCountry;
  language: SiteLanguage;
}
export const defaultSiteLocale: SiteLocale = { country: "au", language: "en" };
export function isSiteCountry(value: string): value is SiteCountry {
  return siteCountries.some((country) => country.id === value);
}
export function isSiteLanguage(value: string): value is SiteLanguage {
  return siteLanguages.some((language) => language.id === value);
}
export function siteLanguageTag({ country, language }: SiteLocale) {
  return `${language}-${siteCountries.find((item) => item.id === country)?.languageTag ?? "AU"}`;
}
/** Retired review-phase prefix; requests to it are redirected permanently. */
export const legacyRegionalPrefix = "/preview/pulse";
/** First path segments served by the regional site for the default country. */
export const regionalRootSegments = ["", "integrations", "about"] as const;

/** Australia in English lives at the root; every other locale carries its own prefix. */
export function regionalPreviewRoot({ country, language }: SiteLocale) {
  if (country === "au" && language === "en") return "/";
  return `/${country}${language === "en" ? "" : `/${language}`}`;
}
export function regionalPreviewPath(locale: SiteLocale, suffix = "") {
  const root = regionalPreviewRoot(locale);
  const rest = suffix.replace(/^\/+/, "");
  if (!rest) return root;
  return `${root === "/" ? "" : root}/${rest}`;
}
/** Splits a regional pathname (current or legacy form) into its locale and page suffix. */
export function parseRegionalPath(pathname: string): { locale: SiteLocale; suffix: string } {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] === "preview" && segments[1] === "pulse") segments.splice(0, 2);
  const hasCountry = isSiteCountry(segments[0] ?? "");
  const country = hasCountry ? (segments.shift() as SiteCountry) : "au";
  const language =
    hasCountry && isSiteLanguage(segments[0] ?? "")
      ? (segments.shift() as SiteLanguage)
      : "en";
  return { locale: { country, language }, suffix: segments.join("/") };
}
export function isRegionalPathname(pathname: string) {
  const first = pathname.split("/").filter(Boolean)[0] ?? "";
  return isSiteCountry(first) || (regionalRootSegments as readonly string[]).includes(first);
}
export function regionalPreviewDestination(
  pathname: string,
  locale: SiteLocale,
) {
  return regionalPreviewPath(locale, parseRegionalPath(pathname).suffix);
}
